"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Crumbs,
  Field,
  Input,
  Page,
  Pill,
  Section,
  Td,
  Tile,
  Tr,
} from "@/components/ui";
import { DataTable, type ColumnConfig } from "@/components/DataTable";
import { HttpError, PerBrokerDealerRow, api } from "@/lib/api";
import { fmtInt, fmtRupees, fmtRupeesShort, pnlClass } from "@/lib/fmt";

export default function PerBrokerDealerPage() {
  const [rows, setRows] = useState<PerBrokerDealerRow[] | null>(null);
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setErr(null);
    api.perBrokerDealer(from || undefined, to || undefined)
      .then(setRows)
      .catch((e) => {
        if (e instanceof HttpError && e.status === 403) setErr("Admin-only report.");
        else setErr(String(e));
      });
  }, [from, to]);

  const totals = useMemo(() => {
    if (!rows) return null;
    return rows.reduce(
      (acc, r) => {
        acc.trades += r.num_trades;
        acc.turnover += Number(r.turnover_rs);
        acc.realized += Number(r.realized_cashflow_rs);
        acc.charges += Number(r.charges_rs);
        return acc;
      },
      { trades: 0, turnover: 0, realized: 0, charges: 0 },
    );
  }, [rows]);

  if (err) return <p className="text-sm text-down">{err}</p>;
  if (!rows) return <p className="eyebrow text-mute">Loading…</p>;

  const cols: ColumnConfig<PerBrokerDealerRow>[] = [
    {
      key: "broker",
      header: "Broker",
      cell: (r) => <Pill variant="outline">{r.broker}</Pill>,
      sortAccessor: (r) => r.broker,
      filterable: true,
      filterValues: (r) => r.broker,
    },
    {
      key: "dealer_id",
      header: "Dealer",
      cell: (r) => r.dealer_id ?? <span className="text-mute">—</span>,
      sortAccessor: (r) => r.dealer_id ?? "",
      filterable: true,
      filterValues: (r) => r.dealer_id ?? "",
    },
    {
      key: "num_accounts",
      header: "# Accounts",
      align: "right",
      cell: (r) => r.num_accounts,
      sortAccessor: (r) => r.num_accounts,
    },
    {
      key: "num_trades",
      header: "# Trades",
      align: "right",
      cell: (r) => fmtInt(r.num_trades),
      sortAccessor: (r) => r.num_trades,
      exportValue: (r) => r.num_trades,
    },
    {
      key: "turnover_rs",
      header: "Turnover",
      align: "right",
      cell: (r) => fmtRupees(r.turnover_rs),
      sortAccessor: (r) => Number(r.turnover_rs),
      exportValue: (r) => Number(r.turnover_rs),
    },
    {
      key: "realized_cashflow_rs",
      header: "Realized cashflow",
      align: "right",
      cell: (r) => (
        <span className={pnlClass(r.realized_cashflow_rs)}>
          {fmtRupees(r.realized_cashflow_rs)}
        </span>
      ),
      sortAccessor: (r) => Number(r.realized_cashflow_rs),
      exportValue: (r) => Number(r.realized_cashflow_rs),
    },
    {
      key: "charges_rs",
      header: "Charges",
      align: "right",
      cell: (r) => <span className="text-mute">{fmtRupees(r.charges_rs)}</span>,
      sortAccessor: (r) => Number(r.charges_rs),
      exportValue: (r) => Number(r.charges_rs),
    },
  ];

  const totalsRow = totals && (
    <Tr>
      <Td mono={false}>
        <span className="font-mono text-sm font-semibold">TOTAL</span>
      </Td>
      <Td><span className="text-mute">—</span></Td>
      <Td align="right"><span className="text-mute">—</span></Td>
      <Td align="right" className="font-semibold">{fmtInt(totals.trades)}</Td>
      <Td align="right" className="font-semibold">{fmtRupees(totals.turnover)}</Td>
      <Td align="right" className={`${pnlClass(totals.realized)} font-semibold`}>
        {fmtRupees(totals.realized)}
      </Td>
      <Td align="right" className="text-mute font-semibold">{fmtRupees(totals.charges)}</Td>
    </Tr>
  );

  return (
    <Page
      eyebrow={<Crumbs items={[{ label: "Reports" }, { label: "Per-broker / dealer" }]} />}
      title={<>Per-broker <em className="italic">rollup</em></>}
    >
      <Section title="Range">
        <div className="flex flex-wrap items-end gap-6">
          <Field label="From"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="To"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
          {(from || to) && (
            <Button onClick={() => { setFrom(""); setTo(""); }}>Clear</Button>
          )}
          <div className="ml-auto eyebrow text-mute">
            {rows.length} (broker, dealer) combination{rows.length === 1 ? "" : "s"}
          </div>
        </div>
      </Section>

      {totals && rows.length > 0 && (
        <section className="grid grid-cols-2 gap-y-6 border-y border-rule py-6 sm:grid-cols-4">
          <Tile label="Trades" value={fmtInt(totals.trades)} />
          <Tile label="Turnover" value={fmtRupeesShort(totals.turnover)} size="hero" className="sm:col-span-2" />
          <Tile label="Charges" value={fmtRupeesShort(totals.charges)} />
        </section>
      )}

      {rows.length === 0 ? (
        <p className="eyebrow text-mute">No trades in range.</p>
      ) : (
        <DataTable
          tableKey="per-broker-dealer"
          stickyFirstColumn
          data={rows}
          columns={cols}
          rowKey={(r) => `${r.broker}::${r.dealer_id ?? "_null"}`}
          totalsRow={totalsRow}
        />
      )}
    </Page>
  );
}
